import {deleteCookie, hasCookie, setCookie} from '@/services/cookie/cookieService';
import log from '@/services/logger/log';
import type {IccSessionInfo} from '@/types/icc/sessionInfo';

import {SSO_COOKIE_CONFIG, SSO_COOKIE_NAMES, type SsoCookieName} from './ssoCookie.consts';

let storedDomain: string | null = null;

export const getStoredDomain = (): string | null => storedDomain;
export const setStoredDomain = (domain: string | null): void => {
    storedDomain = domain;
};

export const extractCookieDomain = (fullUrl: string): string => {
    try {
        const hostname = new URL(fullUrl).hostname;
        const parts = hostname.split('.');
        const domainParts = parts.slice(-2);

        return `.${domainParts.join('.')}`;
    } catch {
        return '';
    }
};

export const setSsoCookies = (sessionInfo: IccSessionInfo): void => {
    // Local dev only: ICC SessionInfo.domain does not match iqos-connect-web-dev.local,
    // so cookies would not be set if we used extractCookieDomain(sessionInfo.domain).
    const domain =
        process.env.REACT_APP_ENV === 'dev'
            ? 'iqos-connect-web-dev.local'
            : sessionInfo.domain
              ? extractCookieDomain(sessionInfo.domain)
              : '';

    if (!domain) {
        log.info('ssoCookieService: setSsoCookies, could not extract domain from SessionInfo');
        return;
    }

    setStoredDomain(domain);

    const cookieValues: Record<SsoCookieName, string | undefined> = {
        gig_jwt: sessionInfo.gigyaToken,
        gig_uid: sessionInfo.gigyaId,
        gig_lt: sessionInfo.gigyaLoginToken,
        hy_at: sessionInfo.hybrisToken,
        hy_rt: sessionInfo.hybrisRefreshToken,
    };

    for (const name of SSO_COOKIE_NAMES) {
        const value = cookieValues[name];

        if (value) {
            setCookie({
                name,
                value,
                encodeValue: false,
                domain,
                path: '/',
                maxAgeSec: SSO_COOKIE_CONFIG[name] * 60,
                secure: true,
                sameSite: 'Lax',
            });
        }
    }
};

export const clearSsoCookies = (): void => {
    const domain = storedDomain;

    for (const name of SSO_COOKIE_NAMES) {
        deleteCookie({name, domain: domain || undefined, path: '/'});
    }

    setStoredDomain(null);
};

export const areSsoCookiesMissing = (): boolean => {
    const result = SSO_COOKIE_NAMES.some((name) => !hasCookie(name));

    return result;
};
